/**
 * Futures (COIN-M) WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    WebsocketStreamsBase,
    WebsocketStream,
    assertParamExists,
    replaceWebsocketStreamsPlaceholders,
    createStreamHandler,
} from '@binance/common';
import type {
    AggregateTradeStreamsResponse,
    AllBookTickersStreamResponse,
    AllMarketLiquidationOrderStreamsResponse,
    AllMarketMiniTickersStreamResponse,
    AllMarketTickersStreamsResponse,
    ContinuousContractKlineCandlestickStreamsResponse,
    ContractInfoStreamResponse,
    DiffBookDepthStreamsResponse,
    IndexKlineCandlestickStreamsResponse,
    IndexPriceStreamResponse,
    IndividualSymbolBookTickerStreamsResponse,
    IndividualSymbolMiniTickerStreamResponse,
    IndividualSymbolTickerStreamsResponse,
    KlineCandlestickStreamsResponse,
    MarkPriceKlineCandlestickStreamsResponse,
    MarkPriceOfAllSymbolsOfAPairResponse,
    MarkPriceStreamResponse,
    MarketLiquidationOrderStreamsResponse,
    PartialBookDepthStreamsResponse,
} from '../types';

const ApiParamCreator = function () {
    return {
        /**
         * The Aggregate Trade Streams push market trade information that is aggregated for fills with same price and taking side every 100 milliseconds.
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 100ms
         *
         * @summary Aggregate Trade Streams
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        aggregateTradeStreams: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('aggregateTradeStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@aggTrade'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Pushes any update to the best bid or ask's price or quantity in real-time for all symbols.
         *
         * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: Real-time
         *
         * @summary All Book Tickers Stream
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allBookTickersStream: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!bookTicker'.slice(1), { id });
        },
        /**
         * The All Liquidation Order Snapshot Streams push force liquidation order information for all symbols in the market. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
         *
         * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 1000ms
         *
         * @summary All Market Liquidation Order Streams
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allMarketLiquidationOrderStreams: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!forceOrder@arr'.slice(1), { id });
        },
        /**
         * 24hr rolling window mini-ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
         *
         * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 1000ms
         *
         * @summary All Market Mini Tickers Stream
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allMarketMiniTickersStream: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!miniTicker@arr'.slice(1), { id });
        },
        /**
         * 24hr rolling window ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
         *
         * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 1000ms
         *
         * @summary All Market Tickers Streams
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allMarketTickersStreams: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!ticker@arr'.slice(1), { id });
        },
        /**
         * Kline update every second
         *
         * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
         *
         * Update Speed: 250ms
         *
         * @summary Continuous Contract Kline/Candlestick Streams
         * @param {string} pair The pair parameter
         * @param {ContinuousContractKlineCandlestickStreamsContractTypeEnum} contractType The contractType parameter
         * @param {ContinuousContractKlineCandlestickStreamsIntervalEnum} interval The interval parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        continuousContractKlineCandlestickStreams: (
            pair: string,
            contractType: ContinuousContractKlineCandlestickStreamsContractTypeEnum,
            interval: ContinuousContractKlineCandlestickStreamsIntervalEnum,
            id?: string
        ): string => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('continuousContractKlineCandlestickStreams', 'pair', pair);
            // verify required parameter 'contractType' is not null or undefined
            assertParamExists(
                'continuousContractKlineCandlestickStreams',
                'contractType',
                contractType
            );
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('continuousContractKlineCandlestickStreams', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders(
                '/<pair>_<contractType>@continuousKline_<interval>'.slice(1),
                { pair, contractType, interval, id }
            );
        },
        /**
         * ContractInfo stream pushes when contract info updates(listing/settlement/contract bracket update). bks field only shows up when bracket gets updated.
         *
         * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: Real-time
         *
         * @summary Contract Info Stream
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        contractInfoStream: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!contractInfo'.slice(1), { id });
        },
        /**
         * Bids and asks, pushed every 250 milliseconds, 500 milliseconds, or 100 milliseconds
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 250ms or 500ms or 100ms
         *
         * @summary Diff. Book Depth Streams
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         * @param {DiffBookDepthStreamsUpdateSpeedEnum} [updateSpeed] WebSocket stream update speed
         *
         * @throws {RequiredError}
         */
        diffBookDepthStreams: (
            symbol: string,
            id?: string,
            updateSpeed?: DiffBookDepthStreamsUpdateSpeedEnum
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('diffBookDepthStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@depth@<updateSpeed>'.slice(1), {
                symbol,
                id,
                updateSpeed,
            });
        },
        /**
         * Index Kline/Candlestick Streams
         *
         * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
         *
         * Update Speed: 250ms
         *
         * @summary Index Kline/Candlestick Streams
         * @param {string} pair The pair parameter
         * @param {IndexKlineCandlestickStreamsIntervalEnum} interval The interval parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        indexKlineCandlestickStreams: (
            pair: string,
            interval: IndexKlineCandlestickStreamsIntervalEnum,
            id?: string
        ): string => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('indexKlineCandlestickStreams', 'pair', pair);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('indexKlineCandlestickStreams', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders(
                '/<pair>@indexPriceKline_<interval>'.slice(1),
                { pair, interval, id }
            );
        },
        /**
         * Index Price Stream
         *
         * Update Speed: 3000ms OR 1000ms
         *
         * @summary Index Price Stream
         * @param {string} pair The pair parameter
         * @param {string} [id] Unique WebSocket request ID.
         * @param {IndexPriceStreamUpdateSpeedEnum} [updateSpeed] WebSocket stream update speed
         *
         * @throws {RequiredError}
         */
        indexPriceStream: (
            pair: string,
            id?: string,
            updateSpeed?: IndexPriceStreamUpdateSpeedEnum
        ): string => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('indexPriceStream', 'pair', pair);

            return replaceWebsocketStreamsPlaceholders(
                '/<pair>@indexPrice@<updateSpeed>'.slice(1),
                { pair, id, updateSpeed }
            );
        },
        /**
         * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: Real-time
         *
         * @summary Individual Symbol Book Ticker Streams
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        individualSymbolBookTickerStreams: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('individualSymbolBookTickerStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@bookTicker'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * 24hr rolling window mini-ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 500ms
         *
         * @summary Individual Symbol Mini Ticker Stream
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        individualSymbolMiniTickerStream: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('individualSymbolMiniTickerStream', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@miniTicker'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * 24hr rolling window ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 500ms
         *
         * @summary Individual Symbol Ticker Streams
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        individualSymbolTickerStreams: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('individualSymbolTickerStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@ticker'.slice(1), { symbol, id });
        },
        /**
         * The Kline/Candlestick Stream push updates to the current klines/candlestick every 250 milliseconds (if existing).
         *
         * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
         *
         * Update Speed: 250ms
         *
         * @summary Kline/Candlestick Streams
         * @param {string} symbol The symbol parameter
         * @param {KlineCandlestickStreamsIntervalEnum} interval The interval parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        klineCandlestickStreams: (
            symbol: string,
            interval: KlineCandlestickStreamsIntervalEnum,
            id?: string
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('klineCandlestickStreams', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('klineCandlestickStreams', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@kline_<interval>'.slice(1), {
                symbol,
                interval,
                id,
            });
        },
        /**
         * Mark Price Kline/Candlestick Streams
         *
         * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
         *
         * Update Speed: 250ms
         *
         * @summary Mark Price Kline/Candlestick Streams
         * @param {string} symbol The symbol parameter
         * @param {MarkPriceKlineCandlestickStreamsIntervalEnum} interval The interval parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        markPriceKlineCandlestickStreams: (
            symbol: string,
            interval: MarkPriceKlineCandlestickStreamsIntervalEnum,
            id?: string
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('markPriceKlineCandlestickStreams', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('markPriceKlineCandlestickStreams', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@markPriceKline_<interval>'.slice(1),
                { symbol, interval, id }
            );
        },
        /**
         * Mark Price of All Symbols of a Pair
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
         *
         * Update Speed: 3000ms OR 1000ms
         *
         * @summary Mark Price of All Symbols of a Pair
         * @param {string} pair The pair parameter
         * @param {string} [id] Unique WebSocket request ID.
         * @param {MarkPriceOfAllSymbolsOfAPairUpdateSpeedEnum} [updateSpeed] WebSocket stream update speed
         *
         * @throws {RequiredError}
         */
        markPriceOfAllSymbolsOfAPair: (
            pair: string,
            id?: string,
            updateSpeed?: MarkPriceOfAllSymbolsOfAPairUpdateSpeedEnum
        ): string => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('markPriceOfAllSymbolsOfAPair', 'pair', pair);

            return replaceWebsocketStreamsPlaceholders('/<pair>@markPrice@<updateSpeed>'.slice(1), {
                pair,
                id,
                updateSpeed,
            });
        },
        /**
         * Mark price update stream
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
         *
         * Update Speed: 3000ms OR 1000ms
         *
         * @summary Mark Price Stream
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         * @param {MarkPriceStreamUpdateSpeedEnum} [updateSpeed] WebSocket stream update speed
         *
         * @throws {RequiredError}
         */
        markPriceStream: (
            symbol: string,
            id?: string,
            updateSpeed?: MarkPriceStreamUpdateSpeedEnum
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('markPriceStream', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@markPrice@<updateSpeed>'.slice(1),
                { symbol, id, updateSpeed }
            );
        },
        /**
         * The Liquidation Order Snapshot Streams push force liquidation order information for specific symbol. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
         *
         * Update Speed: 1000ms
         *
         * @summary Market Liquidation Order Streams
         * @param {string} symbol The symbol parameter
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        marketLiquidationOrderStreams: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('marketLiquidationOrderStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@forceOrder'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Top levels bids and asks.
         *
         * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
         *
         * Update Speed: 250ms, 500ms or 100ms
         *
         * @summary Partial Book Depth Streams
         * @param {string} symbol The symbol parameter
         * @param {PartialBookDepthStreamsLevelsEnum} levels The levels parameter
         * @param {string} [id] Unique WebSocket request ID.
         * @param {PartialBookDepthStreamsUpdateSpeedEnum} [updateSpeed] WebSocket stream update speed
         *
         * @throws {RequiredError}
         */
        partialBookDepthStreams: (
            symbol: string,
            levels: PartialBookDepthStreamsLevelsEnum,
            id?: string,
            updateSpeed?: PartialBookDepthStreamsUpdateSpeedEnum
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('partialBookDepthStreams', 'symbol', symbol);
            // verify required parameter 'levels' is not null or undefined
            assertParamExists('partialBookDepthStreams', 'levels', levels);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@depth<levels>@<updateSpeed>'.slice(1),
                { symbol, levels, id, updateSpeed }
            );
        },
    };
};

/**
 * Api - interface
 * @interface Api
 */
export interface ApiInterface {
    /**
     * The Aggregate Trade Streams push market trade information that is aggregated for fills with same price and taking side every 100 milliseconds.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 100ms
     *
     * @summary Aggregate Trade Streams
     * @param {AggregateTradeStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AggregateTradeStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    aggregateTradeStreams(
        requestParameters: AggregateTradeStreamsRequest
    ): WebsocketStream<AggregateTradeStreamsResponse>;

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for all symbols.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary All Book Tickers Stream
     * @param {AllBookTickersStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllBookTickersStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allBookTickersStream(
        requestParameters?: AllBookTickersStreamRequest
    ): WebsocketStream<AllBookTickersStreamResponse>;

    /**
     * The All Liquidation Order Snapshot Streams push force liquidation order information for all symbols in the market. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Liquidation Order Streams
     * @param {AllMarketLiquidationOrderStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMarketLiquidationOrderStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allMarketLiquidationOrderStreams(
        requestParameters?: AllMarketLiquidationOrderStreamsRequest
    ): WebsocketStream<AllMarketLiquidationOrderStreamsResponse>;

    /**
     * 24hr rolling window mini-ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Mini Tickers Stream
     * @param {AllMarketMiniTickersStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMarketMiniTickersStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allMarketMiniTickersStream(
        requestParameters?: AllMarketMiniTickersStreamRequest
    ): WebsocketStream<AllMarketMiniTickersStreamResponse>;

    /**
     * 24hr rolling window ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Tickers Streams
     * @param {AllMarketTickersStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMarketTickersStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allMarketTickersStreams(
        requestParameters?: AllMarketTickersStreamsRequest
    ): WebsocketStream<AllMarketTickersStreamsResponse>;

    /**
     * Kline update every second
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Continuous Contract Kline/Candlestick Streams
     * @param {ContinuousContractKlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<ContinuousContractKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    continuousContractKlineCandlestickStreams(
        requestParameters: ContinuousContractKlineCandlestickStreamsRequest
    ): WebsocketStream<ContinuousContractKlineCandlestickStreamsResponse>;

    /**
     * ContractInfo stream pushes when contract info updates(listing/settlement/contract bracket update). bks field only shows up when bracket gets updated.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary Contract Info Stream
     * @param {ContractInfoStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<ContractInfoStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    contractInfoStream(
        requestParameters?: ContractInfoStreamRequest
    ): WebsocketStream<ContractInfoStreamResponse>;

    /**
     * Bids and asks, pushed every 250 milliseconds, 500 milliseconds, or 100 milliseconds
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 250ms or 500ms or 100ms
     *
     * @summary Diff. Book Depth Streams
     * @param {DiffBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<DiffBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    diffBookDepthStreams(
        requestParameters: DiffBookDepthStreamsRequest
    ): WebsocketStream<DiffBookDepthStreamsResponse>;

    /**
     * Index Kline/Candlestick Streams
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Index Kline/Candlestick Streams
     * @param {IndexKlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndexKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    indexKlineCandlestickStreams(
        requestParameters: IndexKlineCandlestickStreamsRequest
    ): WebsocketStream<IndexKlineCandlestickStreamsResponse>;

    /**
     * Index Price Stream
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Index Price Stream
     * @param {IndexPriceStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndexPriceStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    indexPriceStream(
        requestParameters: IndexPriceStreamRequest
    ): WebsocketStream<IndexPriceStreamResponse>;

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {IndividualSymbolBookTickerStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndividualSymbolBookTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    individualSymbolBookTickerStreams(
        requestParameters: IndividualSymbolBookTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolBookTickerStreamsResponse>;

    /**
     * 24hr rolling window mini-ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 500ms
     *
     * @summary Individual Symbol Mini Ticker Stream
     * @param {IndividualSymbolMiniTickerStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndividualSymbolMiniTickerStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    individualSymbolMiniTickerStream(
        requestParameters: IndividualSymbolMiniTickerStreamRequest
    ): WebsocketStream<IndividualSymbolMiniTickerStreamResponse>;

    /**
     * 24hr rolling window ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 500ms
     *
     * @summary Individual Symbol Ticker Streams
     * @param {IndividualSymbolTickerStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndividualSymbolTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    individualSymbolTickerStreams(
        requestParameters: IndividualSymbolTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolTickerStreamsResponse>;

    /**
     * The Kline/Candlestick Stream push updates to the current klines/candlestick every 250 milliseconds (if existing).
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Kline/Candlestick Streams
     * @param {KlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    klineCandlestickStreams(
        requestParameters: KlineCandlestickStreamsRequest
    ): WebsocketStream<KlineCandlestickStreamsResponse>;

    /**
     * Mark Price Kline/Candlestick Streams
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Mark Price Kline/Candlestick Streams
     * @param {MarkPriceKlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MarkPriceKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    markPriceKlineCandlestickStreams(
        requestParameters: MarkPriceKlineCandlestickStreamsRequest
    ): WebsocketStream<MarkPriceKlineCandlestickStreamsResponse>;

    /**
     * Mark Price of All Symbols of a Pair
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Mark Price of All Symbols of a Pair
     * @param {MarkPriceOfAllSymbolsOfAPairRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MarkPriceOfAllSymbolsOfAPairResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    markPriceOfAllSymbolsOfAPair(
        requestParameters: MarkPriceOfAllSymbolsOfAPairRequest
    ): WebsocketStream<MarkPriceOfAllSymbolsOfAPairResponse>;

    /**
     * Mark price update stream
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Mark Price Stream
     * @param {MarkPriceStreamRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MarkPriceStreamResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    markPriceStream(
        requestParameters: MarkPriceStreamRequest
    ): WebsocketStream<MarkPriceStreamResponse>;

    /**
     * The Liquidation Order Snapshot Streams push force liquidation order information for specific symbol. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
     *
     * Update Speed: 1000ms
     *
     * @summary Market Liquidation Order Streams
     * @param {MarketLiquidationOrderStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MarketLiquidationOrderStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    marketLiquidationOrderStreams(
        requestParameters: MarketLiquidationOrderStreamsRequest
    ): WebsocketStream<MarketLiquidationOrderStreamsResponse>;

    /**
     * Top levels bids and asks.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 250ms, 500ms or 100ms
     *
     * @summary Partial Book Depth Streams
     * @param {PartialBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<PartialBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    partialBookDepthStreams(
        requestParameters: PartialBookDepthStreamsRequest
    ): WebsocketStream<PartialBookDepthStreamsResponse>;
}

/**
 * Request parameters for aggregateTradeStreams operation in Api.
 * @interface AggregateTradeStreamsRequest
 */
export interface AggregateTradeStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiAggregateTradeStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAggregateTradeStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for allBookTickersStream operation in Api.
 * @interface AllBookTickersStreamRequest
 */
export interface AllBookTickersStreamRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllBookTickersStream
     */
    readonly id?: string;
}

/**
 * Request parameters for allMarketLiquidationOrderStreams operation in Api.
 * @interface AllMarketLiquidationOrderStreamsRequest
 */
export interface AllMarketLiquidationOrderStreamsRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllMarketLiquidationOrderStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for allMarketMiniTickersStream operation in Api.
 * @interface AllMarketMiniTickersStreamRequest
 */
export interface AllMarketMiniTickersStreamRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllMarketMiniTickersStream
     */
    readonly id?: string;
}

/**
 * Request parameters for allMarketTickersStreams operation in Api.
 * @interface AllMarketTickersStreamsRequest
 */
export interface AllMarketTickersStreamsRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllMarketTickersStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for continuousContractKlineCandlestickStreams operation in Api.
 * @interface ContinuousContractKlineCandlestickStreamsRequest
 */
export interface ContinuousContractKlineCandlestickStreamsRequest {
    /**
     * The pair parameter
     * @type {string}
     * @memberof ApiContinuousContractKlineCandlestickStreams
     */
    readonly pair: string;

    /**
     * The contractType parameter
     * @type {'perpetual' | 'current_quarter' | 'next_quarter'}
     * @memberof ApiContinuousContractKlineCandlestickStreams
     */
    readonly contractType: ContinuousContractKlineCandlestickStreamsContractTypeEnum;

    /**
     * The interval parameter
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiContinuousContractKlineCandlestickStreams
     */
    readonly interval: ContinuousContractKlineCandlestickStreamsIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiContinuousContractKlineCandlestickStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for contractInfoStream operation in Api.
 * @interface ContractInfoStreamRequest
 */
export interface ContractInfoStreamRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiContractInfoStream
     */
    readonly id?: string;
}

/**
 * Request parameters for diffBookDepthStreams operation in Api.
 * @interface DiffBookDepthStreamsRequest
 */
export interface DiffBookDepthStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiDiffBookDepthStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiDiffBookDepthStreams
     */
    readonly id?: string;

    /**
     * WebSocket stream update speed
     * @type {'100ms' | '500ms'}
     * @memberof ApiDiffBookDepthStreams
     */
    readonly updateSpeed?: DiffBookDepthStreamsUpdateSpeedEnum;
}

/**
 * Request parameters for indexKlineCandlestickStreams operation in Api.
 * @interface IndexKlineCandlestickStreamsRequest
 */
export interface IndexKlineCandlestickStreamsRequest {
    /**
     * The pair parameter
     * @type {string}
     * @memberof ApiIndexKlineCandlestickStreams
     */
    readonly pair: string;

    /**
     * The interval parameter
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiIndexKlineCandlestickStreams
     */
    readonly interval: IndexKlineCandlestickStreamsIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiIndexKlineCandlestickStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for indexPriceStream operation in Api.
 * @interface IndexPriceStreamRequest
 */
export interface IndexPriceStreamRequest {
    /**
     * The pair parameter
     * @type {string}
     * @memberof ApiIndexPriceStream
     */
    readonly pair: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiIndexPriceStream
     */
    readonly id?: string;

    /**
     * WebSocket stream update speed
     * @type {'1s'}
     * @memberof ApiIndexPriceStream
     */
    readonly updateSpeed?: IndexPriceStreamUpdateSpeedEnum;
}

/**
 * Request parameters for individualSymbolBookTickerStreams operation in Api.
 * @interface IndividualSymbolBookTickerStreamsRequest
 */
export interface IndividualSymbolBookTickerStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiIndividualSymbolBookTickerStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiIndividualSymbolBookTickerStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for individualSymbolMiniTickerStream operation in Api.
 * @interface IndividualSymbolMiniTickerStreamRequest
 */
export interface IndividualSymbolMiniTickerStreamRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiIndividualSymbolMiniTickerStream
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiIndividualSymbolMiniTickerStream
     */
    readonly id?: string;
}

/**
 * Request parameters for individualSymbolTickerStreams operation in Api.
 * @interface IndividualSymbolTickerStreamsRequest
 */
export interface IndividualSymbolTickerStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiIndividualSymbolTickerStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiIndividualSymbolTickerStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for klineCandlestickStreams operation in Api.
 * @interface KlineCandlestickStreamsRequest
 */
export interface KlineCandlestickStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiKlineCandlestickStreams
     */
    readonly symbol: string;

    /**
     * The interval parameter
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiKlineCandlestickStreams
     */
    readonly interval: KlineCandlestickStreamsIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiKlineCandlestickStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for markPriceKlineCandlestickStreams operation in Api.
 * @interface MarkPriceKlineCandlestickStreamsRequest
 */
export interface MarkPriceKlineCandlestickStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiMarkPriceKlineCandlestickStreams
     */
    readonly symbol: string;

    /**
     * The interval parameter
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiMarkPriceKlineCandlestickStreams
     */
    readonly interval: MarkPriceKlineCandlestickStreamsIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiMarkPriceKlineCandlestickStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for markPriceOfAllSymbolsOfAPair operation in Api.
 * @interface MarkPriceOfAllSymbolsOfAPairRequest
 */
export interface MarkPriceOfAllSymbolsOfAPairRequest {
    /**
     * The pair parameter
     * @type {string}
     * @memberof ApiMarkPriceOfAllSymbolsOfAPair
     */
    readonly pair: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiMarkPriceOfAllSymbolsOfAPair
     */
    readonly id?: string;

    /**
     * WebSocket stream update speed
     * @type {'1s'}
     * @memberof ApiMarkPriceOfAllSymbolsOfAPair
     */
    readonly updateSpeed?: MarkPriceOfAllSymbolsOfAPairUpdateSpeedEnum;
}

/**
 * Request parameters for markPriceStream operation in Api.
 * @interface MarkPriceStreamRequest
 */
export interface MarkPriceStreamRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiMarkPriceStream
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiMarkPriceStream
     */
    readonly id?: string;

    /**
     * WebSocket stream update speed
     * @type {'1s'}
     * @memberof ApiMarkPriceStream
     */
    readonly updateSpeed?: MarkPriceStreamUpdateSpeedEnum;
}

/**
 * Request parameters for marketLiquidationOrderStreams operation in Api.
 * @interface MarketLiquidationOrderStreamsRequest
 */
export interface MarketLiquidationOrderStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiMarketLiquidationOrderStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiMarketLiquidationOrderStreams
     */
    readonly id?: string;
}

/**
 * Request parameters for partialBookDepthStreams operation in Api.
 * @interface PartialBookDepthStreamsRequest
 */
export interface PartialBookDepthStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof ApiPartialBookDepthStreams
     */
    readonly symbol: string;

    /**
     * The levels parameter
     * @type {'5' | '10' | '20'}
     * @memberof ApiPartialBookDepthStreams
     */
    readonly levels: PartialBookDepthStreamsLevelsEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiPartialBookDepthStreams
     */
    readonly id?: string;

    /**
     * WebSocket stream update speed
     * @type {'100ms' | '500ms'}
     * @memberof ApiPartialBookDepthStreams
     */
    readonly updateSpeed?: PartialBookDepthStreamsUpdateSpeedEnum;
}

/**
 * Api - interface
 * @class Api
 * @extends {WebsocketStreamsBase}
 */
export class Api implements ApiInterface {
    private readonly websocketBase: WebsocketStreamsBase;
    private localVarParamCreator;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.localVarParamCreator = ApiParamCreator();
    }

    /**
     * The Aggregate Trade Streams push market trade information that is aggregated for fills with same price and taking side every 100 milliseconds.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 100ms
     *
     * @summary Aggregate Trade Streams
     * @param {AggregateTradeStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AggregateTradeStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#aggregate-trade-streams Binance API Documentation}
     */
    public aggregateTradeStreams(
        requestParameters: AggregateTradeStreamsRequest
    ): WebsocketStream<AggregateTradeStreamsResponse> {
        const stream = this.localVarParamCreator.aggregateTradeStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<AggregateTradeStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for all symbols.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary All Book Tickers Stream
     * @param {AllBookTickersStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllBookTickersStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#all-book-tickers-stream Binance API Documentation}
     */
    public allBookTickersStream(
        requestParameters: AllBookTickersStreamRequest = {}
    ): WebsocketStream<AllBookTickersStreamResponse> {
        const stream = this.localVarParamCreator.allBookTickersStream(requestParameters?.id);

        return createStreamHandler<AllBookTickersStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The All Liquidation Order Snapshot Streams push force liquidation order information for all symbols in the market. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Liquidation Order Streams
     * @param {AllMarketLiquidationOrderStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllMarketLiquidationOrderStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#all-market-liquidation-order-streams Binance API Documentation}
     */
    public allMarketLiquidationOrderStreams(
        requestParameters: AllMarketLiquidationOrderStreamsRequest = {}
    ): WebsocketStream<AllMarketLiquidationOrderStreamsResponse> {
        const stream = this.localVarParamCreator.allMarketLiquidationOrderStreams(
            requestParameters?.id
        );

        return createStreamHandler<AllMarketLiquidationOrderStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window mini-ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Mini Tickers Stream
     * @param {AllMarketMiniTickersStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllMarketMiniTickersStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#all-market-mini-tickers-stream Binance API Documentation}
     */
    public allMarketMiniTickersStream(
        requestParameters: AllMarketMiniTickersStreamRequest = {}
    ): WebsocketStream<AllMarketMiniTickersStreamResponse> {
        const stream = this.localVarParamCreator.allMarketMiniTickersStream(requestParameters?.id);

        return createStreamHandler<AllMarketMiniTickersStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window ticker statistics for all symbols. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before. Note that only tickers that have changed will be present in the array.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Tickers Streams
     * @param {AllMarketTickersStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllMarketTickersStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#all-market-tickers-streams Binance API Documentation}
     */
    public allMarketTickersStreams(
        requestParameters: AllMarketTickersStreamsRequest = {}
    ): WebsocketStream<AllMarketTickersStreamsResponse> {
        const stream = this.localVarParamCreator.allMarketTickersStreams(requestParameters?.id);

        return createStreamHandler<AllMarketTickersStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Kline update every second
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Continuous Contract Kline/Candlestick Streams
     * @param {ContinuousContractKlineCandlestickStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<ContinuousContractKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#continuous-contract-kline-candlestick-streams Binance API Documentation}
     */
    public continuousContractKlineCandlestickStreams(
        requestParameters: ContinuousContractKlineCandlestickStreamsRequest
    ): WebsocketStream<ContinuousContractKlineCandlestickStreamsResponse> {
        const stream = this.localVarParamCreator.continuousContractKlineCandlestickStreams(
            requestParameters?.pair,
            requestParameters?.contractType,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<ContinuousContractKlineCandlestickStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * ContractInfo stream pushes when contract info updates(listing/settlement/contract bracket update). bks field only shows up when bracket gets updated.
     *
     * > **After CM migration**, this stream pushes the merged UM + CM universe (subscribable on both `fstream` and `dstream`); each payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary Contract Info Stream
     * @param {ContractInfoStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<ContractInfoStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#contract-info-stream Binance API Documentation}
     */
    public contractInfoStream(
        requestParameters: ContractInfoStreamRequest = {}
    ): WebsocketStream<ContractInfoStreamResponse> {
        const stream = this.localVarParamCreator.contractInfoStream(requestParameters?.id);

        return createStreamHandler<ContractInfoStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Bids and asks, pushed every 250 milliseconds, 500 milliseconds, or 100 milliseconds
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 250ms or 500ms or 100ms
     *
     * @summary Diff. Book Depth Streams
     * @param {DiffBookDepthStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<DiffBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#diff-book-depth-streams Binance API Documentation}
     */
    public diffBookDepthStreams(
        requestParameters: DiffBookDepthStreamsRequest
    ): WebsocketStream<DiffBookDepthStreamsResponse> {
        const stream = this.localVarParamCreator.diffBookDepthStreams(
            requestParameters?.symbol,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<DiffBookDepthStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Index Kline/Candlestick Streams
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Index Kline/Candlestick Streams
     * @param {IndexKlineCandlestickStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndexKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#index-kline-candlestick-streams Binance API Documentation}
     */
    public indexKlineCandlestickStreams(
        requestParameters: IndexKlineCandlestickStreamsRequest
    ): WebsocketStream<IndexKlineCandlestickStreamsResponse> {
        const stream = this.localVarParamCreator.indexKlineCandlestickStreams(
            requestParameters?.pair,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<IndexKlineCandlestickStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Index Price Stream
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Index Price Stream
     * @param {IndexPriceStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndexPriceStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#index-price-stream Binance API Documentation}
     */
    public indexPriceStream(
        requestParameters: IndexPriceStreamRequest
    ): WebsocketStream<IndexPriceStreamResponse> {
        const stream = this.localVarParamCreator.indexPriceStream(
            requestParameters?.pair,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<IndexPriceStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: Real-time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {IndividualSymbolBookTickerStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndividualSymbolBookTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#individual-symbol-book-ticker-streams Binance API Documentation}
     */
    public individualSymbolBookTickerStreams(
        requestParameters: IndividualSymbolBookTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolBookTickerStreamsResponse> {
        const stream = this.localVarParamCreator.individualSymbolBookTickerStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<IndividualSymbolBookTickerStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window mini-ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 500ms
     *
     * @summary Individual Symbol Mini Ticker Stream
     * @param {IndividualSymbolMiniTickerStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndividualSymbolMiniTickerStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#individual-symbol-mini-ticker-stream Binance API Documentation}
     */
    public individualSymbolMiniTickerStream(
        requestParameters: IndividualSymbolMiniTickerStreamRequest
    ): WebsocketStream<IndividualSymbolMiniTickerStreamResponse> {
        const stream = this.localVarParamCreator.individualSymbolMiniTickerStream(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<IndividualSymbolMiniTickerStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window ticker statistics for a single symbol. These are NOT the statistics of the UTC day, but a 24hr rolling window from requestTime to 24hrs before.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 500ms
     *
     * @summary Individual Symbol Ticker Streams
     * @param {IndividualSymbolTickerStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndividualSymbolTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#individual-symbol-ticker-streams Binance API Documentation}
     */
    public individualSymbolTickerStreams(
        requestParameters: IndividualSymbolTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolTickerStreamsResponse> {
        const stream = this.localVarParamCreator.individualSymbolTickerStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<IndividualSymbolTickerStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The Kline/Candlestick Stream push updates to the current klines/candlestick every 250 milliseconds (if existing).
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Kline/Candlestick Streams
     * @param {KlineCandlestickStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<KlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#kline-candlestick-streams Binance API Documentation}
     */
    public klineCandlestickStreams(
        requestParameters: KlineCandlestickStreamsRequest
    ): WebsocketStream<KlineCandlestickStreamsResponse> {
        const stream = this.localVarParamCreator.klineCandlestickStreams(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<KlineCandlestickStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Mark Price Kline/Candlestick Streams
     *
     * > **After CM migration**, both `fstream` and `dstream` may subscribe to CM symbols on this stream.
     *
     * Update Speed: 250ms
     *
     * @summary Mark Price Kline/Candlestick Streams
     * @param {MarkPriceKlineCandlestickStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<MarkPriceKlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#mark-price-kline-candlestick-streams Binance API Documentation}
     */
    public markPriceKlineCandlestickStreams(
        requestParameters: MarkPriceKlineCandlestickStreamsRequest
    ): WebsocketStream<MarkPriceKlineCandlestickStreamsResponse> {
        const stream = this.localVarParamCreator.markPriceKlineCandlestickStreams(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<MarkPriceKlineCandlestickStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Mark Price of All Symbols of a Pair
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Mark Price of All Symbols of a Pair
     * @param {MarkPriceOfAllSymbolsOfAPairRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<MarkPriceOfAllSymbolsOfAPairResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#mark-price-of-all-symbols-of-apair Binance API Documentation}
     */
    public markPriceOfAllSymbolsOfAPair(
        requestParameters: MarkPriceOfAllSymbolsOfAPairRequest
    ): WebsocketStream<MarkPriceOfAllSymbolsOfAPairResponse> {
        const stream = this.localVarParamCreator.markPriceOfAllSymbolsOfAPair(
            requestParameters?.pair,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<MarkPriceOfAllSymbolsOfAPairResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Mark price update stream
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM); both `fstream` and `dstream` may subscribe to either UM or CM symbols on this stream.
     *
     * Update Speed: 3000ms OR 1000ms
     *
     * @summary Mark Price Stream
     * @param {MarkPriceStreamRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<MarkPriceStreamResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#mark-price-stream Binance API Documentation}
     */
    public markPriceStream(
        requestParameters: MarkPriceStreamRequest
    ): WebsocketStream<MarkPriceStreamResponse> {
        const stream = this.localVarParamCreator.markPriceStream(
            requestParameters?.symbol,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<MarkPriceStreamResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The Liquidation Order Snapshot Streams push force liquidation order information for specific symbol. For each symbol，only the latest one liquidation order within 1000ms will be pushed as the snapshot. If no liquidation happens in the interval of 1000ms, no stream will be pushed.
     *
     * Update Speed: 1000ms
     *
     * @summary Market Liquidation Order Streams
     * @param {MarketLiquidationOrderStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<MarketLiquidationOrderStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#market-liquidation-order-streams Binance API Documentation}
     */
    public marketLiquidationOrderStreams(
        requestParameters: MarketLiquidationOrderStreamsRequest
    ): WebsocketStream<MarketLiquidationOrderStreamsResponse> {
        const stream = this.localVarParamCreator.marketLiquidationOrderStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<MarketLiquidationOrderStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Top levels bids and asks.
     *
     * > **After CM migration**, the payload is appended with a new `st` field (`1` = UM, `2` = CM).
     *
     * Update Speed: 250ms, 500ms or 100ms
     *
     * @summary Partial Book Depth Streams
     * @param {PartialBookDepthStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<PartialBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-streams/~#partial-book-depth-streams Binance API Documentation}
     */
    public partialBookDepthStreams(
        requestParameters: PartialBookDepthStreamsRequest
    ): WebsocketStream<PartialBookDepthStreamsResponse> {
        const stream = this.localVarParamCreator.partialBookDepthStreams(
            requestParameters?.symbol,
            requestParameters?.levels,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<PartialBookDepthStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }
}

export enum ContinuousContractKlineCandlestickStreamsContractTypeEnum {
    perpetual = 'perpetual',
    current_quarter = 'current_quarter',
    next_quarter = 'next_quarter',
}

export enum ContinuousContractKlineCandlestickStreamsIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum DiffBookDepthStreamsUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
    UPDATE_SPEED_500ms = '500ms',
}

export enum IndexKlineCandlestickStreamsIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum IndexPriceStreamUpdateSpeedEnum {
    UPDATE_SPEED_1s = '1s',
}

export enum KlineCandlestickStreamsIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum MarkPriceKlineCandlestickStreamsIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum MarkPriceOfAllSymbolsOfAPairUpdateSpeedEnum {
    UPDATE_SPEED_1s = '1s',
}

export enum MarkPriceStreamUpdateSpeedEnum {
    UPDATE_SPEED_1s = '1s',
}

export enum PartialBookDepthStreamsLevelsEnum {
    LEVELS_5 = '5',
    LEVELS_10 = '10',
    LEVELS_20 = '20',
}

export enum PartialBookDepthStreamsUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
    UPDATE_SPEED_500ms = '500ms',
}
